import { useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input } from "@/components/base/Field";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSchoolYear } from "@/contexts/SchoolYearContext";
import { useAsync } from "@/hooks/useAsync";
import {
  generatePeriods,
  getGradeScales,
  getPeriods,
  setPeriodLocked,
  updateGradeScale,
  updatePeriod,
  type GradingPeriod,
} from "@/services/gradeService";
import { formatDate } from "@/utils/format";

/**
 * Notes et périodes : découpage de l'année (trimestres ou semestres), dates de chaque période, verrouillage
 * (plus aucune note modifiable) et barème de chaque cycle (notes sur 10, sur 20...).
 */
export default function GradingPanel() {
  const { notify } = useToast();
  const { can } = useAuth();
  const { consulted } = useSchoolYear();
  const canEdit = can("grading-periods:update");
  const canLock = can("grading-periods:validate");
  const { data, loading, error, reload } = useAsync(
    async () => ({ periods: await getPeriods(consulted), scales: await getGradeScales() }),
    [consulted],
  );
  const [mode, setMode] = useState<"TRIMESTERS" | "SEMESTERS" | null>(null);
  const [editing, setEditing] = useState<GradingPeriod | null>(null);
  const [form, setForm] = useState({ name: "", startDate: "", endDate: "" });
  const [toToggle, setToToggle] = useState<GradingPeriod | null>(null);
  const [scales, setScales] = useState<Record<string, { gradeScale: string; passingAverage: string }>>({});
  const [busy, setBusy] = useState(false);

  if (loading && !data) return <LoadingState />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      notify(message);
      reload();
      return true;
    } catch (err) {
      notify(err instanceof Error ? err.message : "Action impossible.", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const scaleDraft = (cycleId: string) => {
    const saved = data.scales.find((s) => s.cycleId === cycleId);
    return scales[cycleId] ?? { gradeScale: String(saved?.gradeScale ?? 20), passingAverage: String(saved?.passingAverage ?? 10) };
  };

  return (
    <div className="space-y-5">
      <Card>
        <SectionTitle
          icon="ri-calendar-schedule-line"
          title={`Périodes de notes — ${consulted}`}
          subtitle="Les notes et les bulletins sont organisés par période"
        />
        {data.periods.length === 0 ? (
          <p className="mb-4 text-sm text-foreground-600">
            L'année {consulted} n'est pas encore découpée. Choisissez des trimestres ou des semestres : les dates sont proposées
            d'après les dates de l'année, et restent modifiables.
          </p>
        ) : (
          <div className="mb-4 overflow-x-auto rounded-md border border-background-200">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Période</th>
                  <th className="px-3 py-2 font-medium">Dates</th>
                  <th className="px-3 py-2 font-medium">État</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.periods.map((p) => (
                  <tr key={p.id} className="border-t border-background-200">
                    <td className="px-3 py-2.5 font-medium text-foreground-900">{p.name}</td>
                    <td className="px-3 py-2.5 text-foreground-700">
                      {formatDate(p.startDate)} → {formatDate(p.endDate)}
                    </td>
                    <td className="px-3 py-2.5">
                      {p.isLocked ? (
                        <Badge tone="secondary" icon="ri-lock-2-line">
                          Verrouillée
                        </Badge>
                      ) : (
                        <Badge tone="neutral" icon="ri-lock-unlock-line">
                          Ouverte
                        </Badge>
                      )}
                      {p.isLocked && p.lockedByName && <p className="mt-1 text-xs text-foreground-500">par {p.lockedByName}</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="outline"
                            icon="ri-pencil-line"
                            onClick={() => {
                              setEditing(p);
                              setForm({ name: p.name, startDate: p.startDate, endDate: p.endDate });
                            }}
                          >
                            Modifier
                          </Button>
                        )}
                        {canLock && (
                          <Button
                            size="sm"
                            variant={p.isLocked ? "danger" : "primary"}
                            icon={p.isLocked ? "ri-lock-unlock-line" : "ri-lock-2-line"}
                            onClick={() => setToToggle(p)}
                          >
                            {p.isLocked ? "Rouvrir" : "Verrouiller"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant={data.periods.length ? "outline" : "primary"} icon="ri-layout-column-line" onClick={() => setMode("TRIMESTERS")}>
              {data.periods.length ? "Redécouper en 3 trimestres" : "Créer 3 trimestres"}
            </Button>
            <Button variant="outline" icon="ri-layout-2-line" onClick={() => setMode("SEMESTERS")}>
              {data.periods.length ? "Redécouper en 2 semestres" : "Créer 2 semestres"}
            </Button>
          </div>
        )}
        <p className="mt-3 text-xs text-foreground-500">
          Une période verrouillée n'accepte plus aucune note, évaluation ni appréciation : verrouillez-la une fois les notes
          vérifiées, avant d'imprimer les bulletins. Le découpage ne peut plus changer dès qu'une évaluation existe.
        </p>
      </Card>

      <Card>
        <SectionTitle icon="ri-scales-3-line" title="Barème des notes" subtitle="Sur combien les élèves sont notés, par cycle" />
        <div className="space-y-3">
          {data.scales.map((s) => {
            const draft = scaleDraft(s.cycleId);
            return (
              <div key={s.cycleId} className="grid grid-cols-1 items-end gap-3 rounded-md border border-background-200 p-3 sm:grid-cols-[1fr_140px_180px_auto]">
                <p className="text-sm font-medium text-foreground-900">{s.label}</p>
                <Input
                  label="Noté sur"
                  type="number"
                  min={1}
                  disabled={!canEdit}
                  value={draft.gradeScale}
                  onChange={(e) => setScales((prev) => ({ ...prev, [s.cycleId]: { ...draft, gradeScale: e.target.value } }))}
                />
                <Input
                  label="Moyenne d'admission"
                  type="number"
                  min={0}
                  step="0.25"
                  disabled={!canEdit}
                  value={draft.passingAverage}
                  onChange={(e) => setScales((prev) => ({ ...prev, [s.cycleId]: { ...draft, passingAverage: e.target.value } }))}
                />
                {canEdit && (
                  <Button
                    icon="ri-save-3-line"
                    disabled={busy || !scales[s.cycleId]}
                    onClick={() =>
                      run(
                        () => updateGradeScale(s.cycleId, Number(draft.gradeScale), Number(draft.passingAverage)),
                        `Barème du ${s.label.toLowerCase()} enregistré.`,
                      ).then((ok) => ok &&
                          setScales((prev) => {
                            const next = { ...prev };
                            delete next[s.cycleId];
                            return next;
                          }))
                    }
                  >
                    Enregistrer
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-foreground-500">
          Chaque note est ramenée au barème (15/40 vaut 7,5/20). Mentions : Excellent dès 90 % du barème, Très bien 80 %, Bien
          70 %, Assez bien 60 %, Passable à partir de la moyenne d'admission, Insuffisant en dessous.
        </p>
      </Card>

      <ConfirmDialog
        open={mode !== null}
        title={mode === "SEMESTERS" ? "Découper l'année en 2 semestres" : "Découper l'année en 3 trimestres"}
        message={
          data.periods.length
            ? "Les périodes actuelles seront remplacées (possible seulement si aucune évaluation n'y est enregistrée)."
            : `L'année ${consulted} sera découpée en périodes de même durée ; vous pourrez ajuster leurs dates.`
        }
        confirmLabel="Créer les périodes"
        onClose={() => setMode(null)}
        onConfirm={async () => {
          if (mode) await run(() => generatePeriods(consulted, mode), "Périodes de notes créées.");
        }}
      />

      <ConfirmDialog
        open={toToggle !== null}
        title={toToggle?.isLocked ? `Rouvrir le ${toToggle?.name}` : `Verrouiller le ${toToggle?.name}`}
        message={
          toToggle?.isLocked
            ? "Les notes, évaluations et appréciations de la période pourront de nouveau être modifiées."
            : "Plus aucune note, évaluation ni appréciation de la période ne pourra être modifiée. Les bulletins deviennent définitifs."
        }
        confirmLabel={toToggle?.isLocked ? "Rouvrir" : "Verrouiller"}
        onClose={() => setToToggle(null)}
        onConfirm={async () => {
          if (toToggle) {
            await run(
              () => setPeriodLocked(toToggle.id, !toToggle.isLocked),
              toToggle.isLocked ? `${toToggle.name} rouvert.` : `${toToggle.name} verrouillé.`,
            );
          }
        }}
      />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Modifier la période"
        icon="ri-calendar-schedule-line"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button
              icon="ri-save-3-line"
              disabled={busy}
              onClick={async () => {
                if (editing && (await run(() => updatePeriod(editing.id, form), "Période modifiée."))) setEditing(null);
              }}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Nom" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Début" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          <Input label="Fin" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
